import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = [
  {
    q: "Qu'est-ce que CozyHome ?",
    a: "CozyHome est un accompagnement pour les projets de rénovation, d'aménagement, d'extension et de construction. Nous vous aidons à structurer votre projet, préparer les différentes étapes, identifier les professionnels adaptés et suivre son avancement selon le niveau d'accompagnement choisi.",
  },
  {
    q: "Est-ce que CozyHome est simplement une plateforme de mise en relation ?",
    a: "Non. La mise en relation avec des professionnels peut faire partie du parcours, mais l'objectif de CozyHome est plus large : vous accompagner dans la préparation et l'organisation de votre projet, puis dans son suivi lorsque l'accompagnement choisi le prévoit.",
  },
  {
    q: "À quel moment puis-je contacter CozyHome ?",
    a: "Le plus tôt possible. Vous pouvez nous contacter lorsque votre projet n'est encore qu'une idée, lorsque vous disposez déjà de plans ou de devis, ou lorsque vous cherchez à structurer les prochaines étapes.",
  },
  {
    q: "Quels projets pouvez-vous accompagner ?",
    a: "CozyHome est conçu pour accompagner différents projets d'habitat : rénovation, aménagement intérieur, extension, transformation et construction. La faisabilité et les prestations nécessaires sont ensuite étudiées selon chaque projet.",
  },
  {
    q: "Dois-je déjà connaître mon budget ?",
    a: "Non. Une première enveloppe, même approximative, nous aide cependant à comprendre votre projet et à orienter les premières réflexions.",
  },
  {
    q: "Dois-je déjà avoir des plans ?",
    a: "Non. Selon la nature du projet, des prestations de conception, plans ou démarches administratives peuvent être nécessaires et intégrées au parcours.",
  },
  {
    q: "Puis-je travailler avec un artisan que je connais déjà ?",
    a: "Oui. Vous avez déjà un professionnel avec lequel vous souhaitez travailler ? Parlez-nous-en. Nous étudierons avec vous la meilleure manière de l'intégrer au projet.",
  },
  {
    q: "Comment sont choisis les professionnels ?",
    a: "CozyHome développe un réseau de professionnels partenaires selon des critères définis pour le réseau et les besoins des projets.",
  },
  {
    q: "Quelle différence entre Support et Expertise ?",
    a: "Support convient aux clients qui souhaitent rester impliqués dans le suivi de leur chantier tout en bénéficiant d'un interlocuteur en cas de besoin. Expertise propose un accompagnement renforcé pendant les travaux, avec des interventions régulières, le suivi de différents éléments du chantier et des comptes rendus.",
  },
  {
    q: "Combien coûte l'accompagnement CozyHome ?",
    a: "Le coût dépend du projet, des prestations nécessaires et du niveau d'accompagnement choisi. Les conditions et tarifs vous sont présentés clairement avant votre engagement.",
  },
  {
    q: "Puis-je partir d'un modèle CozyHome et le personnaliser ?",
    a: "Oui. Les modèles CozyHome sont conçus comme des points de départ pour imaginer votre future maison. Les possibilités d'adaptation dépendront notamment du terrain, de la réglementation applicable, des contraintes techniques et du projet souhaité.",
  },
  {
    q: "CozyHome intervient-il partout en France ?",
    a: "CozyHome se développe progressivement par secteurs afin de conserver un accompagnement de proximité. Indiquez la localisation de votre projet lors de votre demande : nous vous préciserons les possibilités d'accompagnement dans votre secteur.",
  },
  {
    q: "Comment commencer ?",
    a: "Très simplement : remplissez le questionnaire CozyHome pour nous présenter votre projet.",
  },
];

export default function FaqPage() {
  const navigate = useNavigate();
  return (
    <PageShell>
      <PageHero
        testid="page-faq-hero"
        label="Questions fréquentes"
        title={<>Votre projet soulève des questions ? <span className="italic text-sage">C'est normal.</span></>}
        sub="Voici les réponses aux principales questions pour mieux comprendre le fonctionnement de CozyHome."
      />

      <section className="bg-ivory pb-20 sm:pb-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <Accordion type="single" collapsible data-testid="faq-list" className="flex flex-col gap-3">
              {FAQ.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`q-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="rounded-2xl border border-[#EDEAE3] bg-white px-6 transition-colors duration-300 data-[state=open]:border-cognac/40"
                >
                  <AccordionTrigger className="py-5 text-left text-sm font-bold text-navy hover:no-underline sm:text-base">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-relaxed text-stone-500 sm:text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal>
            <div className="mt-14 text-center">
              <p className="text-sm text-stone-500">Votre question n'y figure pas ?</p>
              <button
                data-testid="faq-cta"
                onClick={() => navigate("/estimer")}
                className="group mt-4 inline-flex items-center gap-2.5 rounded-full bg-navy px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-sage"
              >
                Parler de mon projet
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
